import {
  Anchor,
  Arrow,
  Content,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-popover';
import classNames from 'classnames';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { UIPortalContext } from '../ui-theme/UITheme';
import styles from './Popover.module.css';

type PopoverPlacement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

/** Chakra's delay, kept so migrated popovers feel the same. */
const DEFAULT_OPEN_DELAY = 200;
/** Also Chakra's: long enough to move the pointer onto the surface. */
const DEFAULT_CLOSE_DELAY = 200;

const side = (placement: PopoverPlacement) =>
  placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';

const align = (placement: PopoverPlacement) =>
  (placement.split('-')[1] ?? 'center') as 'start' | 'end' | 'center';

type RadixContentProps = ComponentProps<typeof Content>;

interface PopoverBaseProps
  extends Omit<RadixContentProps, 'asChild' | 'side' | 'align' | 'children'> {
  /** Surface content, or a function given a `close` for content that dismisses itself. */
  children: ReactNode | ((api: { close: () => void }) => ReactNode);
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Also open on pointer hover. The surface keeps itself open while hovered. */
  openOnHover?: boolean;
  openDelay?: number;
  closeDelay?: number;
  placement?: PopoverPlacement;
  arrow?: boolean;
  /** Block interaction with the rest of the page while open. */
  modal?: boolean;
  portal?: boolean;
  sideOffset?: number;
  'data-testid'?: string;
}

/**
 * A popover positions against exactly one element. Normally that is its own
 * trigger. Pass `anchor` instead when something else already owns the open
 * state — a trigger toggles on click, which shuts a popover that the same
 * press just opened.
 */
type PopoverAnchoring =
  | {
      /** Must be a single element — it receives the trigger's props. */
      trigger: ReactNode;
      anchor?: never;
    }
  | {
      /** Must be a single element — it receives the positioning ref only. */
      anchor: ReactNode;
      trigger?: never;
    };

export type PopoverProps = PopoverBaseProps & PopoverAnchoring;

/**
 * Popover surface built on Radix.
 *
 * One component rather than a compound set: measured across the 32 Chakra
 * popover sites in this app, 47% render only a body or no body at all, so the
 * parts were mostly ceremony. `trigger` takes the anchor and `children` the
 * content — the shape `SummaryPopover` and `FilterPopover` had already arrived
 * at independently.
 *
 * Dismissal is the part worth reading before using it. Radix closes on
 * pointer-down *outside* the content, where Chakra closed on *blur* — and a
 * child that portals elsewhere (a react-select menu, a nested tree picker) is
 * outside by that measure even though the user is still working inside the
 * popover. Chakra's version survived those only because react-select
 * `preventDefault`s mousedown so focus never left. Any call site with a
 * portaled interactive child must therefore keep itself open explicitly:
 *
 *     <Popover
 *       trigger={…}
 *       onInteractOutside={(event) => {
 *         if ((event.target as HTMLElement).closest('.my-menu-portal')) {
 *           event.preventDefault();
 *         }
 *       }}
 *     >
 *
 * One dependency rule keeps that behaviour honest, and it is not expressible in
 * `package.json` itself. Radix's layer stack — which layer owns an Escape, which
 * one an outside click — lives in a module-scoped `Set` inside
 * `@radix-ui/react-dismissable-layer`. Two copies of that package mean two
 * stacks that cannot see each other, and every layer then believes it is the
 * top one: a single Escape meant for a menu nested in a popover unwinds both.
 * So `@radix-ui/react-popover` and `@radix-ui/react-dropdown-menu` are pinned to
 * exact versions that resolve to the SAME dismissable-layer (1.1.11):
 * `react-popover@1.1.15` and `react-dropdown-menu@2.1.16`. Before bumping
 * either, check `pnpm why @radix-ui/react-dismissable-layer` still reports one
 * version. The pin buys a second thing: `react-popover@1.1.23` hard-codes
 * `deferPointerDownOutside`, which moves dismissal from the pointer-down to the
 * following click and then skips it whenever that click's propagation was
 * stopped. A filter chip's remove button does exactly that, so any popover open
 * over the page stays open behind it — where Chakra, and 1.1.15, close.
 */
export const Popover = ({
  trigger,
  anchor,
  children,
  open,
  defaultOpen = false,
  onOpenChange,
  openOnHover = false,
  openDelay = DEFAULT_OPEN_DELAY,
  closeDelay = DEFAULT_CLOSE_DELAY,
  placement = 'bottom',
  arrow = false,
  modal,
  portal = true,
  className,
  onOpenAutoFocus,
  sideOffset,
  ...contentProps
}: PopoverProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  // Where the surface portals to. Normally document.body; inside a Modal it is
  // the <dialog> itself, because the top layer sits above every z-index and a
  // body-portaled surface would paint under the backdrop and be inert.
  const portalTarget = useContext(UIPortalContext);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Read through a ref so an inline handler does not change the identity of the
  // hover callbacks, which would re-arm the timers on every render.
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) clearTimeout(openTimer.current);
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChangeRef.current?.(next);
    },
    [isControlled]
  );

  const close = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers, setOpen]);

  const hoverHandlers = useMemo(
    () =>
      openOnHover
        ? {
            onPointerEnter: () => {
              clearTimers();
              openTimer.current = setTimeout(() => setOpen(true), openDelay);
            },
            onPointerLeave: () => {
              clearTimers();
              closeTimer.current = setTimeout(() => setOpen(false), closeDelay);
            },
          }
        : {},
    [openOnHover, openDelay, closeDelay, clearTimers, setOpen]
  );

  const content = (
    <Content
      className={classNames(styles.content, className)}
      side={side(placement)}
      align={align(placement)}
      // The arrow is drawn into the gap, so an arrowed popover needs a smaller
      // offset to sit as close to its trigger as the Chakra one did.
      sideOffset={sideOffset ?? (arrow ? 2 : 8)}
      // A hover popover must not pull focus off whatever the user is doing; a
      // click-opened one keeps Radix's default focus move.
      onOpenAutoFocus={
        onOpenAutoFocus ??
        (openOnHover ? (event) => event.preventDefault() : undefined)
      }
      {...hoverHandlers}
      {...contentProps}
    >
      {typeof children === 'function' ? children({ close }) : children}
      {arrow ? (
        // Radix's default arrow is a filled SVG polygon, which cannot carry the
        // surface's border on its two outer edges without also drawing a seam
        // across its base. `asChild` swaps in our own element inside Radix's
        // positioned wrapper: a square rotated 45°, bordered on the two sides
        // that face away from the surface, sitting half inside it — the same
        // construction Chakra's popper arrow used. See Popover.module.css.
        <Arrow asChild width={12} height={6}>
          <span
            className={styles.arrow}
            data-testid="popover-arrow"
            aria-hidden="true"
          />
        </Arrow>
      ) : null}
    </Content>
  );

  return (
    <Root
      open={isOpen}
      onOpenChange={(next) => {
        clearTimers();
        setOpen(next);
      }}
      modal={modal}
    >
      {anchor !== undefined ? (
        <Anchor asChild {...hoverHandlers}>
          {anchor}
        </Anchor>
      ) : (
        <Trigger asChild {...hoverHandlers}>
          {trigger}
        </Trigger>
      )}
      {portal ? <Portal container={portalTarget}>{content}</Portal> : content}
    </Root>
  );
};
